import { Injectable } from '@angular/core';
import { WclService } from './wcl-service.service';
import { StringHelper } from '../../helpers/strings.helper';
import { StorageKeys } from '../../../shared/models/storage-keys';
import { AppConfig } from '../../../shared/config/app-config';
import { LoanProduct } from './loan-analytics.service';
@Injectable({
    providedIn: 'root',
})
export class LoanPrimerService {
    error = false;
    errorMessage: any;
    isLoading = false;
    renewalRequestState = false;

    constructor(private wclService: WclService, private config: AppConfig) {
        this.renewalRequestState = history?.state?.data?.isRenewal;
    }

    async handleSeeWhatYouCanGet(loanProduct: LoanProduct) {
        this.isLoading = true;

        if (this.useUnifiedProvisionalOfferFlow) {
            return this.seeWhatYouCanGetV2(loanProduct);
        } else {
            return this.seeWhatYouCanGetV1();
        }
    }

    private async seeWhatYouCanGetV1() {
        const state = this.wclService.getCurrentLoanRequestState();
        const isRenewal = history.state.data?.isRenewal;

        if (isRenewal) {
            this.isLoading = true;
            try {
                await this.wclService.createLoanRenewalRequest();
                this.wclService.goToPage('/loan-renewal/loan-requirements');
            } catch (err) {
                this.error = true;
                this.errorMessage = StringHelper.getErrorMessageHttpErrorResponse(err);
            } finally {
                this.isLoading = false;
            }
        } else if (!state.loanRequestExists && state.isPrimaryBusinessSelected) {
            // If no loan request exists but primary business is selected,
            // take them to provisional offer info page
            this.wclService.goToPage('/provisional-offer/info');
        } else {
            // Otherwise take them to select business page
            this.wclService.goToPage('/select-business/home');
        }
    }

    private async seeWhatYouCanGetV2(loanProduct) {
        try {
            const ongoingRequestData = await this.getOngoingRequestData();
            const isRenewal = await this.evaluateRenewalMode(ongoingRequestData);

            if (this.error) return;

            if (isRenewal) {
                await this.handleLoanRenewalRequest(loanProduct);
            } else {
                await this.handleScratchLoanRequest(ongoingRequestData, loanProduct);
            }
        } catch (error) {
            this.error = true;
            this.errorMessage = StringHelper.getErrorMessageHttpErrorResponse(error);
        } finally {
            this.isLoading = false;
        }
    }

    private async getOngoingRequestData() {
        const cachedOngoingData = this.wclService.getCurrentLoanRequestState();
        if (cachedOngoingData) {
            return cachedOngoingData;
        }

        try {
            const res = await this.wclService.getBusinessOnGoingLoanService();
            localStorage.setItem(StorageKeys.ONGOING_LOAN_DATA, JSON.stringify(res));
            return res;
        } catch (err) {
            this.error = true;
            this.isLoading = false;
            this.errorMessage = StringHelper.getErrorMessageHttpErrorResponse(err);
            throw new Error(`error, ${err}`);
        }
    }

    async evaluateRenewalMode(requestData) {
        const requestStatus = requestData?.loanRequestResponse?.requestStatus;
        const terminalStatuses = ['REJECTED', 'EXPIRED', 'TERMINATED', 'CLOSED', 'ELIGIBILITY_EXPIRED','NO_LOAN_OFFER', 'OFFER_EXPIRED', 'FAILED_ELIGIBILITY_CHECK', 'FAILED_PROVISIONAL_ELIGIBILITY_CHECK', 'PROVISIONAL_OFFER_DENIED', 'PROVISIONAL_OFFER_EXPIRED', 'PROVISIONAL_ELIGIBILITY_EXPIRED', 'FINAL_OFFER_DECLINED', 'GUARANTOR_AGREEMENT_FAILED', 'BORROWER_AGREEMENT_FAILED'];

        if (requestData.loanRequestResponse?.expired || terminalStatuses.includes(requestStatus) || this.renewalRequestState) {
            return true;
        } else {
            try {
                const res = await this.wclService.checkifLoanPreviouslyGrantedService();
                return res.status;
            } catch (err) {
                this.error = true;
                this.isLoading = false;
                this.errorMessage = StringHelper.getErrorMessageHttpErrorResponse(err);
                throw new Error(`error, ${err}`);
            }
        }
    }

    async handleScratchLoanRequest(requestData, loanProduct) {
        if (requestData.isPrimaryBusinessSelected) {
            // If no loan request exists but primary business is selected,
            // take them to provisional offer info page
            this.wclService.goToPage('/provisional-offer/info');
        } else {
            // Otherwise take them to select business page
            this.wclService.setActiveLoanProduct(loanProduct);
            this.wclService.goToPage('/select-business/home');
        }
    }

    async handleLoanRenewalRequest(loanProduct) {
        // first call to reapply
        try {
            await this.wclService.reapplyForLoan({
                loanProduct: loanProduct,
            });
            // then go to entry point
            this.wclService.goToEntryPoint();
        } catch (err) {
            this.error = true;
            this.errorMessage = StringHelper.getErrorMessageHttpErrorResponse(err);
        } finally {
            this.isLoading = false;
        }
    }

    reloadOnError(loanProduct) {
        this.isLoading = false;
        this.error = false;
        this.handleSeeWhatYouCanGet(loanProduct);
    }

    get useUnifiedProvisionalOfferFlow() {
        return this.wclService.useUnifiedLoanRequestFlow;
    }
}
